import React, { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import {
  FaHome,
  FaCalendarAlt,
  FaNewspaper,
  FaImages,
  FaCog,
  FaEnvelope,
  FaUsers,
  FaGift,
  FaSignOutAlt,
  FaBars,
} from "react-icons/fa";
import "./AdminLayout.css";

const AdminLayout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Force Hebrew language for admin panel
  React.useEffect(() => {
    // Save the current language to restore later when leaving admin
    const currentLanguage = i18n.language;
    
    // Force Hebrew for admin
    if (currentLanguage !== 'he') {
      i18n.changeLanguage('he');
    }
    
    // Always set RTL for admin
    document.dir = 'rtl';
    document.body.classList.remove('ltr');
    document.body.classList.add('rtl');
    
    // Cleanup function to restore language when leaving admin
    return () => {
      // Restore the original language when unmounting
      const storedLang = localStorage.getItem('i18nextLng');
      if (storedLang && storedLang !== 'he') {
        i18n.changeLanguage(storedLang);
        document.dir = storedLang === 'he' ? 'rtl' : 'ltr';
        document.body.classList.remove('rtl', 'ltr');
        document.body.classList.add(storedLang === 'he' ? 'rtl' : 'ltr');
      }
    };
  }, [i18n]);

  const handleLogout = () => {
    logout();
  };

  const menuItems = [
    { icon: <FaHome />, text: t("admin.dashboard"), path: "/admin/dashboard" },
    {
      icon: <FaCalendarAlt />,
      text: t("admin.workshops"),
      path: "/admin/workshops",
    },
    { icon: <FaNewspaper />, text: t("admin.blog"), path: "/admin/blog" },
    { icon: <FaImages />, text: t("admin.media"), path: "/admin/media" },
    { icon: <FaEnvelope />, text: t("admin.contact"), path: "/admin/contact" },
    {
      icon: <FaUsers />,
      text: t("admin.registrations"),
      path: "/admin/registrations",
    },
    {
      icon: <FaGift />,
      text: t("admin.giftVoucher"),
      path: "/admin/gift-voucher",
    },
    { icon: <FaCog />, text: t("admin.settings"), path: "/admin/settings" },
  ];

  return (
    <div className="layout-container">
      <button
        className="mobile-menu-button"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <FaBars />
      </button>

      <aside className={`sidebar${isSidebarOpen ? " open" : ""}`}>
        {menuItems.map((item, index) => (
          <div
            key={index}
            className="menu-item"
            onClick={() => {
              navigate(item.path);
              setIsSidebarOpen(false);
            }}
          >
            <div className="menu-icon">{item.icon}</div>
            <span className="menu-text">{item.text}</span>
          </div>
        ))}

        <div className="menu-item" onClick={handleLogout}>
          <div className="menu-icon">
            <FaSignOutAlt />
          </div>
          <span className="menu-text">{t("admin.logout")}</span>
        </div>
      </aside>

      <main className="main-content main-content">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
